import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import {
  alpha,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  InputAdornment,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import {
  Search as SearchIcon,
  NotificationsActive as NotificationsActiveIcon,
  DraftsOutlined as DraftsOutlinedIcon,
  UpdateOutlined as UpdateOutlinedIcon,
  ArrowOutward as ArrowOutwardIcon
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import FilterSelectInput from 'src/common/FilterSelectInput';
import Loader from 'src/common/Loader';
import useAdminSiteFilter from 'src/hooks/useAdminSiteFilter';
import { setErrorInfo } from 'src/redux/modalSlice';
import {
  getNotificationsList,
  markAllNotificationsAsRead,
  getUnreadNotificationsCount,
  loadMoreNotifications,
  markNotificationAsRead
} from 'src/redux/notificationSlice';
import { getFormattedDate } from 'src/utils/getInitials';
import {
  getNotificationQueryParams,
  getNotificationTargetRoute,
  TYRE_REPORT_NOTIFICATION_EVENT,
  TYRE_RETREAD_NOTIFICATION_EVENT
} from 'src/utils/notificationUtils';
import { isTyreManagementV2Enabled } from 'src/utils/tyreManagementFeature';

const PAGE_SIZE = 10;
const NOTIFICATION_STATUS_TABS = [
  { value: 'read', label: 'Read' },
  { value: 'unread', label: 'Unread' },
  { value: 'updated', label: 'Updated' }
];
const NOTIFICATION_TYPE_OPTIONS = [
  { id: 'all', name: 'All notifications' },
  { id: TYRE_RETREAD_NOTIFICATION_EVENT, name: 'Tyre alerts' },
  { id: TYRE_REPORT_NOTIFICATION_EVENT, name: 'Tyre reports' },
];

const getModelAccent = (model) => {
  switch (model?.toLowerCase()) {
    case 'transaction':
      return {
        bg: alpha('#2563EB', 0.12),
        color: '#2563EB',
        label: 'Transaction'
      };
    case 'vehicle':
      return {
        bg: alpha('#059669', 0.12),
        color: '#059669',
        label: 'Vehicle'
      };
    case 'card':
      return {
        bg: alpha('#7C3AED', 0.12),
        color: '#7C3AED',
        label: 'Card'
      };
    case 'tyrealert':
      return {
        bg: alpha('#EA580C', 0.12),
        color: '#C2410C',
        label: 'Tyre alert'
      };
    case 'tyrereportjob':
      return {
        bg: alpha('#0891B2', 0.12),
        color: '#0E7490',
        label: 'Tyre report'
      };
    default:
      return {
        bg: alpha('#6B7280', 0.12),
        color: '#4B5563',
        label: model || 'Notification'
      };
  }
};

const isTyreNotification = (notification) => (
  [TYRE_RETREAD_NOTIFICATION_EVENT, TYRE_REPORT_NOTIFICATION_EVENT]
    .includes(notification?.event_type)
  || notification?.target_model?.model?.toLowerCase() === 'tyrealert'
);

const Notifications = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const {
    isAdmin,
    siteFilter,
    setSiteFilter,
    siteOptions
  } = useAdminSiteFilter();
  const {
    loading,
    totalCount,
    unreadCount,
    notificationsList
  } = useSelector((state) => state.notifications);
  const appSettings = useSelector((state) => state.settings.appSettings);
  const tyreManagementV2Enabled = isTyreManagementV2Enabled(appSettings);

  const [searchInput, setSearchInput] = useState('');
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('unread');
  const [eventType, setEventType] = useState(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get('event_type')
      || (searchParams.get('target_model') === 'tyrealert'
        ? TYRE_RETREAD_NOTIFICATION_EVENT
        : 'all');
  });
  const [pageNo, setPageNo] = useState(1);
  const [markingAllRead, setMarkingAllRead] = useState(false);
  const [markingReadIds, setMarkingReadIds] = useState([]);

  const effectiveEventType = !tyreManagementV2Enabled && [
    TYRE_RETREAD_NOTIFICATION_EVENT,
    TYRE_REPORT_NOTIFICATION_EVENT,
  ].includes(eventType)
    ? 'all'
    : eventType;
  const notificationTypeOptions = useMemo(() => (
    tyreManagementV2Enabled
      ? NOTIFICATION_TYPE_OPTIONS
      : NOTIFICATION_TYPE_OPTIONS.filter((option) => ![
        TYRE_RETREAD_NOTIFICATION_EVENT,
        TYRE_REPORT_NOTIFICATION_EVENT,
      ].includes(option.id))
  ), [tyreManagementV2Enabled]);
  const visibleNotifications = useMemo(() => (
    tyreManagementV2Enabled
      ? notificationsList
      : notificationsList.filter((notification) => !isTyreNotification(notification))
  ), [notificationsList, tyreManagementV2Enabled]);
  const visibleUnreadCount = tyreManagementV2Enabled
    ? unreadCount
    : visibleNotifications.filter((item) => !item.is_read && !item.is_updated).length;

  const params = useMemo(() => getNotificationQueryParams({
    searchText,
    siteFilter,
    statusFilter,
    eventType: effectiveEventType
  }), [effectiveEventType, searchText, siteFilter, statusFilter]);

  useEffect(() => {
    if (!tyreManagementV2Enabled && [
      TYRE_RETREAD_NOTIFICATION_EVENT,
      TYRE_REPORT_NOTIFICATION_EVENT,
    ].includes(eventType)) setEventType('all');
  }, [eventType, tyreManagementV2Enabled]);

  useEffect(() => {
    setPageNo(1);
    dispatch(getNotificationsList({ limit: PAGE_SIZE, pageNo: 1, params }));
  }, [dispatch, params]);

  const hasMore = visibleNotifications.length < totalCount;

  const handleSearch = () => {
    setSearchText(searchInput);
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    setMarkingReadIds((prev) => (
      prev.includes(notificationId) ? prev : [...prev, notificationId]
    ));

    try {
      await dispatch(markNotificationAsRead(notificationId)).unwrap();
      dispatch(getUnreadNotificationsCount());
      setPageNo(1);
      dispatch(getNotificationsList({ limit: PAGE_SIZE, pageNo: 1, params }));
    } catch (error) {
      dispatch(setErrorInfo({
        errorMsg: error?.error_message || 'Failed to mark notification as read.'
      }));
    } finally {
      setMarkingReadIds((prev) => prev.filter((id) => id !== notificationId));
    }
  };

  const handleMarkAllAsRead = async () => {
    setMarkingAllRead(true);

    try {
      await dispatch(markAllNotificationsAsRead()).unwrap();
      dispatch(getUnreadNotificationsCount());
      setPageNo(1);
      dispatch(getNotificationsList({ limit: PAGE_SIZE, pageNo: 1, params }));
    } catch (error) {
      dispatch(setErrorInfo({
        errorMsg: error?.error_message || 'Failed to mark all notifications as read.'
      }));
    } finally {
      setMarkingAllRead(false);
    }
  };

  const handleOpenNotification = async (notification) => {
    const targetRoute = getNotificationTargetRoute(notification, {
      tyreManagementEnabled: tyreManagementV2Enabled,
    });

    if (!targetRoute) {
      return;
    }

    if (!notification.is_read) {
      await handleMarkAsRead(notification.id);
    }

    history.push(targetRoute);
  };

  const handleLoadMore = () => {
    const nextPage = pageNo + 1;
    setPageNo(nextPage);
    dispatch(loadMoreNotifications({ limit: PAGE_SIZE, pageNo: nextPage, params }));
  };

  if (loading && notificationsList.length === 0) {
    return <Loader />;
  }

  return (
    <>
      <Helmet>
        <title>Notifications | Retrack FMS</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: '#F8FAFC',
          minHeight: 'calc(100vh - 64px)',
          py: 2
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3
          }}
        >
          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', lg: 'center' }}
            spacing={2}
            sx={{ py: 2 }}
          >
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  color: '#111827',
                  letterSpacing: '-0.02em',
                  mb: 0.5
                }}
              >
                NOTIFICATIONS
              </Typography>
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                Review alerts, transaction updates, and linked records in one place.
              </Typography>
            </Box>

            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
              {visibleUnreadCount > 0 && statusFilter === 'unread' && (
                <Button
                  variant="outlined"
                  onClick={handleMarkAllAsRead}
                  disabled={markingAllRead}
                  sx={{
                    height: 38,
                    px: 2,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 700
                  }}
                >
                  {markingAllRead ? <CircularProgress size={18} /> : 'Mark all as read'}
                </Button>
              )}
              <Chip
                label={`Unread: ${visibleUnreadCount}`}
                sx={{
                  height: 38,
                  px: 1,
                  bgcolor: alpha('#2563EB', 0.1),
                  color: '#1D4ED8',
                  fontWeight: 700,
                  borderRadius: 2
                }}
              />
              <Chip
                label={`Total: ${tyreManagementV2Enabled ? totalCount : visibleNotifications.length}`}
                sx={{
                  height: 38,
                  px: 1,
                  bgcolor: '#FFFFFF',
                  color: '#0F172A',
                  fontWeight: 700,
                  border: '1px solid #CBD5E1',
                  borderRadius: 2
                }}
              />
            </Stack>
          </Stack>

          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            spacing={2}
            sx={{
              p: 2,
              bgcolor: '#FFFFFF',
              borderRadius: 3,
              border: '1px solid #E2E8F0',
              alignItems: { xs: 'stretch', lg: 'center' }
            }}
          >
            <TextField
              size="small"
              placeholder="Search notifications"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              onKeyDown={handleSearchKeyDown}
              sx={{
                flex: 1,
                minWidth: 0,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: '#F8FAFC'
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                )
              }}
            />

            {isAdmin && (
              <FilterSelectInput
                label="Site"
                name="site"
                value={siteFilter}
                data={siteOptions}
                onInputChange={setSiteFilter}
                sx={{ width: { xs: '100%', lg: 200 }, mr: 0 }}
              />
            )}

            <FilterSelectInput
              label="Type"
              name="notificationType"
              value={effectiveEventType}
              data={notificationTypeOptions}
              onInputChange={setEventType}
              sx={{ width: { xs: '100%', lg: 190 }, mr: 0 }}
            />

            <Tabs
              value={statusFilter}
              onChange={(_, nextStatusFilter) => setStatusFilter(nextStatusFilter)}
              variant="fullWidth"
              sx={{
                width: { xs: '100%', lg: 300 },
                minHeight: 40,
                p: 0.4,
                borderRadius: 2,
                bgcolor: '#F8FAFC',
                border: '1px solid #E2E8F0',
                '& .MuiTabs-indicator': {
                  display: 'none'
                },
                '& .MuiTab-root': {
                  minHeight: 32,
                  minWidth: 0,
                  px: 1.5,
                  borderRadius: 1.5,
                  color: '#64748B',
                  fontWeight: 800,
                  textTransform: 'none'
                },
                '& .Mui-selected': {
                  color: '#1D4ED8',
                  bgcolor: '#FFFFFF',
                  boxShadow: '0 8px 18px rgba(15, 23, 42, 0.08)'
                }
              }}
            >
              {NOTIFICATION_STATUS_TABS.map((tab) => (
                <Tab
                  key={tab.value}
                  value={tab.value}
                  label={tab.label}
                />
              ))}
            </Tabs>

            <Button
              variant="contained"
              onClick={handleSearch}
              sx={{
                minHeight: 40,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 700,
                px: 3
              }}
            >
              Apply
            </Button>
          </Stack>

          <Stack spacing={2}>
            {visibleNotifications.map((notification) => {
              let accentModel = notification?.target_model?.model;
              if (notification?.event_type === TYRE_REPORT_NOTIFICATION_EVENT) {
                accentModel = 'tyrereportjob';
              } else if (isTyreNotification(notification)) {
                accentModel = 'tyrealert';
              }
              const modelAccent = getModelAccent(accentModel);
              const targetRoute = getNotificationTargetRoute(notification, {
                tyreManagementEnabled: tyreManagementV2Enabled,
              });
              const isUpdated = Boolean(notification.is_updated);
              const isRead = Boolean(notification.is_read);
              const isUnread = !isRead && !isUpdated;
              const isMarkingThisRead = markingAllRead || markingReadIds.includes(notification.id);
              let cardBorder = '1px solid rgba(37, 99, 235, 0.18)';
              let cardShadow = '0 18px 36px rgba(37, 99, 235, 0.08)';
              let notificationIcon = <NotificationsActiveIcon />;
              let statusChip = null;

              if (isUpdated) {
                cardBorder = '1px solid rgba(124, 58, 237, 0.22)';
                cardShadow = '0 18px 36px rgba(124, 58, 237, 0.08)';
                notificationIcon = <UpdateOutlinedIcon />;
                statusChip = (
                  <Chip
                    size="small"
                    label="Updated"
                    sx={{
                      bgcolor: alpha('#7C3AED', 0.1),
                      color: '#7C3AED',
                      fontWeight: 700
                    }}
                  />
                );
              } else if (isRead) {
                cardBorder = '1px solid #E5E7EB';
                cardShadow = '0 10px 24px rgba(15, 23, 42, 0.05)';
                notificationIcon = <DraftsOutlinedIcon />;
              } else {
                statusChip = (
                  <Chip
                    size="small"
                    label="Unread"
                    sx={{
                      bgcolor: alpha('#DC2626', 0.1),
                      color: '#DC2626',
                      fontWeight: 700
                    }}
                  />
                );
              }

              return (
                <Box
                  key={notification.id}
                  sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: 3,
                    bgcolor: '#FFFFFF',
                    border: cardBorder,
                    boxShadow: cardShadow,
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 22px 40px rgba(15, 23, 42, 0.09)'
                    }
                  }}
                >
                  {isUnread && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 18,
                        left: 0,
                        width: 4,
                        height: 56,
                        borderRadius: '0 999px 999px 0',
                        background: 'linear-gradient(180deg, #2563EB 0%, #60A5FA 100%)'
                      }}
                    />
                  )}

                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    sx={{ p: { xs: 2.25, md: 2.75 } }}
                  >
                    <Stack direction="row" spacing={2} sx={{ minWidth: 0, flex: 1 }}>
                      <Avatar
                        sx={{
                          bgcolor: modelAccent.bg,
                          color: modelAccent.color,
                          width: 52,
                          height: 52,
                          fontWeight: 800
                        }}
                      >
                        {notificationIcon}
                      </Avatar>

                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={1}
                          alignItems={{ xs: 'flex-start', sm: 'center' }}
                          sx={{ mb: 1 }}
                        >
                          <Typography variant="h6" sx={{ fontWeight: 800, color: '#0F172A' }}>
                            {notification.title}
                          </Typography>
                          <Chip
                            size="small"
                            label={modelAccent.label}
                            sx={{
                              bgcolor: modelAccent.bg,
                              color: modelAccent.color,
                              fontWeight: 700
                            }}
                          />
                          {statusChip}
                        </Stack>

                        <Typography variant="body1" sx={{ color: '#334155', mb: 1.5, lineHeight: 1.65 }}>
                          {notification.message}
                        </Typography>

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} flexWrap="wrap">
                          <Chip
                            label={notification.site_name || 'No Site'}
                            size="small"
                            variant="outlined"
                          />
                          {notification?.target_model?.display_name && (
                            <Chip
                              label={`Ref: ${notification.target_model.display_name}`}
                              size="small"
                              variant="outlined"
                            />
                          )}
                          <Chip
                            label={getFormattedDate(notification.created_on)}
                            size="small"
                            variant="outlined"
                          />
                        </Stack>
                      </Box>
                    </Stack>

                    <Stack
                      direction={{ xs: 'row', md: 'column' }}
                      spacing={1}
                      justifyContent="center"
                      sx={{ flexShrink: 0 }}
                    >
                      {!isRead && (
                        <Button
                          variant="outlined"
                          onClick={() => handleMarkAsRead(notification.id)}
                          disabled={isMarkingThisRead}
                          sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 700,
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {isMarkingThisRead ? <CircularProgress size={18} /> : 'Mark as read'}
                        </Button>
                      )}
                      <Button
                        variant="contained"
                        endIcon={<ArrowOutwardIcon />}
                        onClick={() => handleOpenNotification(notification)}
                        disabled={!targetRoute}
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 700,
                          whiteSpace: 'nowrap'
                        }}
                      >
                        Open
                      </Button>
                    </Stack>
                  </Stack>
                </Box>
              );
            })}

            {!loading && notificationsList.length === 0 && (
              <Box
                sx={{
                  borderRadius: 3,
                  p: 5,
                  textAlign: 'center',
                  bgcolor: '#FFFFFF',
                  border: '1px dashed #CBD5E1'
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
                  No notifications found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Try adjusting search text or filters.
                </Typography>
              </Box>
            )}

            {hasMore && (
              <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
                <Button
                  variant="outlined"
                  onClick={handleLoadMore}
                  disabled={loading}
                  sx={{
                    minWidth: 180,
                    borderRadius: 999,
                    textTransform: 'none',
                    fontWeight: 700
                  }}
                >
                  {loading ? <CircularProgress size={18} /> : 'Load more'}
                </Button>
              </Box>
            )}
          </Stack>
        </Container>
      </Box>
    </>
  );
};

export default Notifications;
